import { useCallback, useMemo, useState } from "react";
import Select from "react-select";
import ExcelJS from "exceljs";
import { Chart as ChartJS, type ChartConfiguration } from "chart.js/auto";
import { getThemeClasses } from "../../utils/ThemeClasses";
import { useDarkMode } from "../../context/ThemeContext";
import { getCustomSelectStyles } from "../../Layouts/admin/shared";
import {
  useDailyHistoryRange,
  useDailyHistoryYears,
  useDailyHistoryMovements,
} from "../../features/dashboards/queries";
import {
  aggregate,
  aggWord,
  autoGranularity,
  rangeToWindow,
  spanInDays,
  todayMxISO,
  type AggPoint,
  type Granularity,
  type RangePreset,
} from "../../features/dashboards/dailyHistoryAgg";
import type { ReportingPlatform } from "../../features/dashboards/reportingConfig";
import {
  ActiveUnitsChart,
  AltasBajasChart,
  EffectivenessChart,
} from "./DailyHistoryCharts";
import DeviceMovementsModal from "./DeviceMovementsModal";

const addDaysISO = (iso: string, n: number): string => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n, 12));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
};

interface Opt<T extends string> {
  value: T;
  label: string;
}

const RANGE_OPTS: Opt<RangePreset>[] = [
  { value: "1m", label: "1 mes" },
  { value: "3m", label: "3 meses" },
  { value: "6m", label: "6 meses" },
  { value: "ytd", label: "Año en curso" },
  { value: "1y", label: "1 año" },
  { value: "2y", label: "2 años" },
  { value: "all", label: "Todo el histórico" },
];

const GRAN_OPTS: Opt<"auto" | Granularity>[] = [
  { value: "auto", label: "Automática" },
  { value: "day", label: "Día" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
];

const RANGE_OPTS_UI: Opt<string>[] = [
  ...RANGE_OPTS,
  { value: "custom", label: "Personalizado…" },
];

interface ActiveUnitsHistorySectionProps {
  themeClasses: ReturnType<typeof getThemeClasses>;
  platform?: ReportingPlatform;
  platformLabel?: string;
  showActiveUnits?: boolean;
  showAltasBajas?: boolean;
  showEffectiveness?: boolean;
}

export default function ActiveUnitsHistorySection({
  themeClasses,
  platform,
  platformLabel,
  showActiveUnits = true,
  showAltasBajas = true,
  showEffectiveness = true,
}: ActiveUnitsHistorySectionProps) {
  const { isDarkMode } = useDarkMode();
  const compactStyles = useMemo(() => {
    const base: any = getCustomSelectStyles(isDarkMode);
    const wrap =
      (key: string, extra: any) =>
      (provided: any, state: any) => ({
        ...(base[key] ? base[key](provided, state) : provided),
        ...extra,
      });
    return {
      ...base,
      control: wrap("control", { minHeight: 30, fontSize: 12 }),
      valueContainer: wrap("valueContainer", { padding: "0 6px" }),
      dropdownIndicator: wrap("dropdownIndicator", { padding: 4 }),
    } as any;
  }, [isDarkMode]);

  const [range, setRange] = useState<RangePreset>("1m");
  const [gran, setGran] = useState<"auto" | Granularity>("auto");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [dayModal, setDayModal] = useState<{
    from: string;
    to: string;
    label: string;
  } | null>(null);

  const yearsQuery = useDailyHistoryYears();
  const maxISO = todayMxISO();
  const minYear = yearsQuery.data?.length
    ? Math.min(...yearsQuery.data)
    : Number(maxISO.slice(0, 4)) - 1;
  const minISO = `${minYear}-01-01`;

  const useCustom = Boolean(customFrom && customTo && customFrom <= customTo);
  const window = useCustom
    ? { from: customFrom, to: customTo }
    : rangeToWindow(range, minISO, maxISO);

  const historyQuery = useDailyHistoryRange(window);
  const rows = useMemo(() => historyQuery.data?.points ?? [], [historyQuery.data]);

  const movementsQuery = useDailyHistoryMovements(
    showAltasBajas ? window : undefined,
  );
  const rangeMovements = useMemo(
    () => movementsQuery.data ?? [],
    [movementsQuery.data],
  );

  const effectiveGran: Granularity =
    gran === "auto" ? autoGranularity(spanInDays(window.from, window.to)) : gran;

  const points = useMemo(
    () => aggregate(rows, effectiveGran, platform),
    [rows, effectiveGran, platform],
  );

  const loading = historyQuery.isLoading || yearsQuery.isLoading;
  const showDividers = effectiveGran === "day";

  const handlePointClick = useCallback(
    (p: AggPoint) =>
      setDayModal({
        from: p.key,
        to: addDaysISO(p.key, p.days - 1),
        label: p.fullLabel,
      }),
    [],
  );

  const handleDownloadExcel = async () => {
    if (isDownloading || points.length === 0) return;
    setIsDownloading(true);
    try {
      const labels = points.map((p) => p.label);

      const makeChartImage = (config: ChartConfiguration) => {
        const canvas = document.createElement("canvas");
        canvas.width = 1600;
        canvas.height = 700;
        const chart = new ChartJS(canvas.getContext("2d")!, config);
        const url = canvas.toDataURL("image/png", 1.0);
        chart.destroy();
        return url;
      };
      const baseOpts = {
        animation: false as const,
        responsive: false,
        plugins: { legend: { labels: { color: "#000", font: { size: 16 } } } },
        scales: {
          x: { ticks: { color: "#000", font: { size: 13 } }, grid: { color: "#E5E7EB" } },
          y: { ticks: { color: "#000", font: { size: 13 } }, grid: { color: "#E5E7EB" } },
        },
      };

      const activeImg = showActiveUnits
        ? makeChartImage({
            type: "line",
            data: {
              labels,
              datasets: [
                {
                  label: "Unidades activas",
                  data: points.map((p) => p.active),
                  borderColor: "#0d9488",
                  backgroundColor: "rgba(13,148,136,0.15)",
                  fill: true,
                  tension: 0.35,
                  pointRadius: 0,
                  spanGaps: false,
                },
              ],
            },
            options: baseOpts,
          })
        : null;

      const flowImg = showAltasBajas
        ? makeChartImage({
            type: "line",
            data: {
              labels,
              datasets: [
                {
                  label: "Altas",
                  data: points.map((p) => p.altas),
                  borderColor: "#22c55e",
                  backgroundColor: "rgba(34,197,94,0.15)",
                  fill: "origin",
                  tension: 0.4,
                  pointRadius: 0,
                  spanGaps: false,
                },
                {
                  label: "Bajas",
                  data: points.map((p) => (p.bajas == null ? null : -p.bajas)),
                  borderColor: "#ef4444",
                  backgroundColor: "rgba(239,68,68,0.15)",
                  fill: "origin",
                  tension: 0.4,
                  pointRadius: 0,
                  spanGaps: false,
                },
              ],
            },
            options: {
              ...baseOpts,
              scales: {
                ...baseOpts.scales,
                y: {
                  ...baseOpts.scales.y,
                  ticks: {
                    color: "#000",
                    font: { size: 13 },
                    callback: (v: string | number) => Math.abs(Number(v)),
                  },
                },
              },
            },
          })
        : null;

      const effImg = showEffectiveness
        ? makeChartImage({
            type: "line",
            data: {
              labels,
              datasets: [
                {
                  label: "Efectividad (%)",
                  data: points.map((p) => p.effectiveness),
                  borderColor: "#6366f1",
                  backgroundColor: "rgba(99,102,241,0.15)",
                  fill: true,
                  tension: 0.35,
                  pointRadius: 0,
                  spanGaps: false,
                },
              ],
            },
            options: {
              ...baseOpts,
              scales: {
                ...baseOpts.scales,
                y: {
                  ...baseOpts.scales.y,
                  min: 0,
                  max: 100,
                  ticks: {
                    color: "#000",
                    font: { size: 13 },
                    callback: (v: string | number) => `${v}%`,
                  },
                },
              },
            },
          })
        : null;

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Dumax";
      const ws = workbook.addWorksheet("Reporte", {
        views: [{ showGridLines: false }],
      });

      ws.mergeCells(1, 1, 1, labels.length + 1);
      ws.getCell(1, 1).value = `Señales Dumax — ${window.from} al ${window.to} (${aggWord(effectiveGran, "level")})`;
      ws.getCell(1, 1).font = { bold: true, size: 14, color: { argb: "FF0D9488" } };

      const rowMap: [string, (i: number) => number | null][] = [
        ["Periodo", () => null],
        ...(showActiveUnits
          ? ([["Unidades activas (prom.)", (i: number) => points[i].active]] as [
              string,
              (i: number) => number | null,
            ][])
          : []),
        ...(showAltasBajas
          ? ([
              ["Altas (suma)", (i: number) => points[i].altas],
              ["Bajas (suma)", (i: number) => points[i].bajas],
            ] as [string, (i: number) => number | null][])
          : []),
        ...(showEffectiveness
          ? ([
              ["Efectividad (%)", (i: number) => points[i].effectiveness],
            ] as [string, (i: number) => number | null][])
          : []),
      ];
      rowMap.forEach(([title, get], r) => {
        const row = ws.getRow(3 + r);
        row.getCell(1).value = title;
        row.getCell(1).font = { bold: true };
        points.forEach((p, i) => {
          row.getCell(i + 2).value = r === 0 ? p.label : get(i);
        });
      });

      [activeImg, flowImg, effImg]
        .filter((img): img is string => Boolean(img))
        .forEach((img, idx) => {
          ws.addImage(
            workbook.addImage({
              base64: img.replace(/^data:image\/png;base64,/, ""),
              extension: "png",
            }),
            { tl: { col: 0, row: 8 + idx * 22 }, ext: { width: 820, height: 360 } },
          );
        });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `senales-dumax_${window.from}_${window.to}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error("[ActiveUnitsHistory] Error generando Excel:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const aggLevel = aggWord(effectiveGran, "level");
  const aggFlow = aggWord(effectiveGran, "flow");

  const labelCls = `text-[10px] font-medium uppercase tracking-wide ${themeClasses.textSecondary}`;

  const controls = (
    <div className="flex flex-wrap items-end gap-2 w-full sm:w-auto">
      <div className="flex flex-col gap-1 flex-1 min-w-[130px] sm:flex-none sm:w-36">
        <span className={labelCls}>Rango</span>
        <Select
          options={RANGE_OPTS_UI}
          value={
            useCustom
              ? { value: "custom", label: "Personalizado…" }
              : RANGE_OPTS.find((o) => o.value === range)
          }
          onChange={(opt) => {
            const v = (opt as Opt<string>).value;
            if (v === "custom") {
              setCustomOpen(true);
              return;
            }
            setRange(v as RangePreset);
            setCustomFrom("");
            setCustomTo("");
            setCustomOpen(false);
          }}
          styles={compactStyles}
          isSearchable={false}
          menuPortalTarget={document.body}
        />
      </div>
      <div className="flex flex-col gap-1 flex-1 min-w-[120px] sm:flex-none sm:w-32">
        <span className={labelCls}>Granularidad</span>
        <Select
          options={GRAN_OPTS}
          value={GRAN_OPTS.find((o) => o.value === gran)}
          onChange={(opt) => setGran((opt as Opt<"auto" | Granularity>).value)}
          styles={compactStyles}
          isSearchable={false}
          menuPortalTarget={document.body}
        />
      </div>
      {(customOpen || useCustom) && (
        <div className="flex flex-col gap-1 w-full sm:w-auto">
          <span className={labelCls}>Rango exacto</span>
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={customFrom}
              min={minISO}
              max={maxISO}
              onChange={(e) => setCustomFrom(e.target.value)}
              className={`flex-1 sm:flex-none px-2 py-1.5 rounded-md text-xs border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text}`}
            />
            <span className={`text-xs ${themeClasses.textSecondary}`}>→</span>
            <input
              type="date"
              value={customTo}
              min={minISO}
              max={maxISO}
              onChange={(e) => setCustomTo(e.target.value)}
              className={`flex-1 sm:flex-none px-2 py-1.5 rounded-md text-xs border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text}`}
            />
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={handleDownloadExcel}
        disabled={isDownloading || loading || points.length === 0}
        title="Exportar Excel del periodo"
        className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs transition-colors ${
          isDownloading || loading || points.length === 0
            ? "bg-primary-500/60 text-gray-900 cursor-not-allowed"
            : "bg-primary-500 text-gray-900 hover:bg-primary-400"
        }`}
      >
        <i
          className={
            isDownloading ? "ri-loader-4-line animate-spin" : "ri-file-excel-2-line"
          }
        />
      </button>
    </div>
  );

  const metricParts = [
    showEffectiveness && "Efectividad",
    showActiveUnits && "Unidades activas",
    showAltasBajas && "Altas y bajas",
  ].filter(Boolean) as string[];
  const headerTitle = platformLabel
    ? `Historial diario — ${platformLabel}`
    : "Historial de Unidades";
  const headerSubtitle = `${metricParts.join(" · ")} por periodo`;
  const effTitle = platformLabel ? `Efectividad — ${platformLabel}` : "Efectividad";
  const flowTitle = platformLabel ? `Altas y Bajas — ${platformLabel}` : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h3 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
          {headerTitle}
        </h3>
        <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
          {headerSubtitle}
        </p>
      </div>
      {showEffectiveness && (
        <EffectivenessChart
          themeClasses={themeClasses}
          points={points}
          title={effTitle}
          aggLabel={aggLevel}
          showMonthDividers={showDividers}
          controls={controls}
          loading={loading}
        />
      )}
      {showActiveUnits && (
        <ActiveUnitsChart
          themeClasses={themeClasses}
          points={points}
          aggLabel={aggLevel}
          showMonthDividers={showDividers}
          controls={controls}
          loading={loading}
        />
      )}
      {showAltasBajas && (
        <AltasBajasChart
          themeClasses={themeClasses}
          points={points}
          title={flowTitle}
          aggLabel={aggFlow}
          showMonthDividers={showDividers}
          controls={controls}
          loading={loading}
          onPointClick={handlePointClick}
        />
      )}
      {dayModal && (
        <DeviceMovementsModal
          themeClasses={themeClasses}
          title={`Altas y bajas · ${dayModal.label}`}
          devices={rangeMovements.filter(
            (m) =>
              m.date >= dayModal.from &&
              m.date <= dayModal.to &&
              (platform ? m.platform === platform : true),
          )}
          loading={movementsQuery.isLoading}
          showType
          showPlatform={!platform}
          onClose={() => setDayModal(null)}
        />
      )}
    </div>
  );
}
